import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Wand2, 
  ChevronDown, 
  ChevronUp, 
  Loader2, 
  Search,
  SlidersHorizontal,
  TableProperties,
  AlertCircle,
  Gauge,
  ChevronRight,
  Check,
  Plus,
  X,
  Lightbulb,
} from 'lucide-react';
import Pill, { PillGroup, OutputFieldPill } from './Pill';
import WeightSlider from './WeightSlider';
import ResultEstimateIndicator, { ResultEstimateBadge } from '../shared/ResultEstimateIndicator';
import usePredictiveFiltering from '../../hooks/usePredictiveFiltering';
import { useLookups } from '../../hooks/useLookups';
import api from '../../utils/axios';

// Filter types that support custom user-added values
const CUSTOMIZABLE_FILTERS = {
  jobTitle: { label: 'Job Title', placeholder: 'e.g., VP of Sales' },
  managementLevel: { label: 'Seniority', placeholder: 'e.g., Director' },
  industryCodes: { label: 'Industry', placeholder: 'e.g., Healthcare' },
  companyName: { label: 'Company', placeholder: 'e.g., Google' },
  state: { label: 'Location', placeholder: 'e.g., California' },
  city: { label: 'City', placeholder: 'e.g., San Francisco' },
};

// Quick add filter options for categories that may not exist
const QUICK_ADD_FILTERS = [
  { id: 'jobTitle', label: 'Job Title' },
  { id: 'managementLevel', label: 'Seniority' },
  { id: 'companyName', label: 'Company' },
  { id: 'state', label: 'Location' },
  { id: 'industryCodes', label: 'Industry' },
];

/**
 * PillBuilder Component
 * 
 * Main component for building search queries using editable pills.
 * Shows AI interpretation as pills that users can toggle.
 * 
 * Props:
 * - query: The search query string
 * - mode: Search mode (candidate, client, company, jobs, demand)
 * - onFiltersChange: Callback when filters change
 * - onSearch: Callback to execute search with current filters
 * - initialFilters: Optional initial filter state
 * - showOutputFields: Whether to show output field configuration
 */
function PillBuilder({
  query,
  mode = 'people',
  location,
  onFiltersChange,
  onSearch,
  initialFilters,
  initialOutputFields,
  showOutputFields = true,
  compact = false,
}) {
  // Normalize mode prop - convert unified search modes to valid backend modes
  // Using useMemo ensures stable reference across HMR updates
  const normalizedMode = useMemo(() => {
    const validModes = ['people', 'candidate', 'client', 'company', 'jobs', 'demand'];
    if (validModes.includes(mode)) return mode;
    // Handle unified search mode formats
    if (mode === 'contacts-candidates' || mode === 'candidates') return 'people';
    if (mode === 'contacts-clients' || mode === 'clients') return 'people';
    if (mode === 'companies') return 'company';
    if (mode === 'candidate-placement' || mode === 'mpc') return 'people';
    return 'people'; // Default fallback
  }, [mode]);
  
  // State
  const [filters, setFilters] = useState(initialFilters || []);
  const [outputFields, setOutputFields] = useState(initialOutputFields || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [estimatedResults, setEstimatedResults] = useState(null);
  const [sampleResults, setSampleResults] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [showOutputConfig, setShowOutputConfig] = useState(false);
  const [interpretationNotes, setInterpretationNotes] = useState('');
  const [showWeights, setShowWeights] = useState(false);
  const [filterWeights, setFilterWeights] = useState({}); // { filterId: weight (0-100) }
  const [resultsLimit, setResultsLimit] = useState(25); // Default results limit
  const [loadingPhase, setLoadingPhase] = useState(0); // For conversational loading
  const [suggestedMode, setSuggestedMode] = useState(null); // Mode recommendation
  const [nextSteps, setNextSteps] = useState([]); // Guided recommendations
  const [needsConfirmation, setNeedsConfirmation] = useState(false); // Confirmation gate
  const [confirmed, setConfirmed] = useState(false); // User has confirmed interpretation
  const [pendingSearch, setPendingSearch] = useState(null); // Pending search data
  const [previousEstimate, setPreviousEstimate] = useState(null); // For trend tracking
  
  // Custom filter input state
  const [addingToFilter, setAddingToFilter] = useState(null); // Which filter ID is being added to
  const [customFilterValue, setCustomFilterValue] = useState(''); // Text input value
  const customInputRef = useRef(null); // Ref for focusing input
  
  // Predictive filtering hook - proactively detect which pills would return 0 results
  const { 
    getPillState, 
    isCalculating: isPredicting,
    getExpansionSuggestions,
  } = usePredictiveFiltering(filters, normalizedMode, estimatedResults);
  
  // Mode detection keywords
  const MODE_KEYWORDS = {
    jobs: ['job', 'jobs', 'role', 'roles', 'position', 'positions', 'hiring', 'opening', 'openings', 'career', 'vacancy', 'vacancies'],
    demand: ['funding', 'raised', 'series a', 'series b', 'series c', 'ipo', 'acquisition', 'merger', 'news', 'signal', 'signals', 'announcement'],
    people: ['engineer', 'developer', 'designer', 'manager', 'director', 'analyst', 'specialist', 'consultant', 'nurse', 'doctor', 'lawyer',
             'ceo', 'cfo', 'cto', 'coo', 'vp', 'vice president', 'founder', 'owner', 'executive', 'decision maker', 'buyer'],
  };
  
  // Detect if query suggests a different mode
  const detectSuggestedMode = useCallback((queryText, currentMode) => {
    const lowerQuery = queryText.toLowerCase();
    
    for (const [modeId, keywords] of Object.entries(MODE_KEYWORDS)) {
      if (modeId !== currentMode) {
        for (const keyword of keywords) {
          if (lowerQuery.includes(keyword)) {
            // Map mode IDs to display names
            const modeNames = {
              jobs: { id: 'jobs', name: 'Jobs', reason: `Your search mentions "${keyword}"` },
              demand: { id: 'demand', name: 'Signals', reason: `Your search mentions "${keyword}"` },
              people: { id: 'people', name: 'People', reason: `Looking for people matching this profile` },
            };
            return modeNames[modeId];
          }
        }
      }
    }
    return null;
  }, []);
  
  // Generate next step recommendations based on filters
  const generateNextSteps = useCallback((filterData, searchMode) => {
    const steps = [];
    
    // Check for missing location
    const hasLocation = filterData.some(f => 
      ['State', 'City', 'Country', 'Location'].includes(f.label) && 
      f.pills?.some(p => p.status === 'included')
    );
    if (!hasLocation) {
      steps.push({
        type: 'refine',
        text: 'Add a location to narrow results',
        action: 'Add city or state',
      });
    }
    
    // Check for missing industry
    const hasIndustry = filterData.some(f => 
      f.label === 'Industry' && f.pills?.some(p => p.status === 'included')
    );
    if (!hasIndustry && (searchMode === 'people' || searchMode === 'candidate' || searchMode === 'client')) {
      steps.push({
        type: 'refine',
        text: 'Specify an industry for better targeting',
        action: 'Add industry filter',
      });
    }
    
    // Mode-specific suggestions
    if (searchMode === 'people' || searchMode === 'candidate' || searchMode === 'client') {
      steps.push({
        type: 'tip',
        text: 'Try adding skills or years of experience',
      });
    } else if (searchMode === 'jobs') {
      steps.push({
        type: 'tip',
        text: 'Add salary range or remote preference',
      });
    } else if (searchMode === 'demand') {
      steps.push({
        type: 'tip',
        text: 'Filter by timeframe to see the latest signals',
      });
    }
    
    return steps.slice(0, 2); // Max 2 recommendations
  }, []);
  
  // Debounce timer ref
  const debounceRef = React.useRef(null);
  
  // Track active request to prevent concurrent requests
  const activeRequestRef = React.useRef(null);
  
  // Track if we have cached/initial filters to avoid unnecessary AI calls
  const hasInitialFilters = initialFilters && initialFilters.length > 0;
  const [useCachedFilters, setUseCachedFilters] = useState(hasInitialFilters);
  
  // Sync with initialFilters when they change (from parent)
  // Use JSON comparison to avoid unnecessary updates from reference changes
  const initialFiltersJson = JSON.stringify(initialFilters || []);
  const initialOutputFieldsJson = JSON.stringify(initialOutputFields || []);
  
  useEffect(() => {
    if (initialFilters && initialFilters.length > 0) {
      // Only update if content actually changed
      const currentJson = JSON.stringify(filters);
      if (currentJson !== initialFiltersJson) {
        console.log('🔄 PillBuilder: Syncing with initialFilters (content changed)');
        setFilters(initialFilters);
      }
    }
    if (initialOutputFields && initialOutputFields.length > 0) {
      const currentOutputJson = JSON.stringify(outputFields);
      if (currentOutputJson !== initialOutputFieldsJson) {
        setOutputFields(initialOutputFields);
      }
    }
  }, [initialFiltersJson, initialOutputFieldsJson]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Fetch interpretation when query or mode changes - but skip if using cached filters
  useEffect(() => {
    if (!query || query.trim().length < 3) {
      setFilters([]);
      setOutputFields([]);
      setConfidence(null);
      setEstimatedResults(null);
      return;
    }
    
    // If we have initial/cached filters, use them instead of calling AI
    if (useCachedFilters && initialFilters && initialFilters.length > 0) {
      // Already synced via the effect above, just notify parent once
      if (onFiltersChange) {
        setTimeout(() => {
          onFiltersChange(initialFilters, initialOutputFields || outputFields);
        }, 0);
      }
      return;
    }
    
    // Debounce the interpretation request
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      // Cancel any active request
      if (activeRequestRef.current) {
        activeRequestRef.current.abort();
        activeRequestRef.current = null;
      }
      fetchInterpretation(query, normalizedMode, location);
    }, 500);
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, normalizedMode, location, useCachedFilters]);
  
  // Fetch interpretation from backend with timeout
  const fetchInterpretation = async (queryText, searchMode, loc) => {
    // Validate inputs before making request
    const trimmedQuery = queryText?.trim() || '';
    
    // Validate query
    if (!trimmedQuery || trimmedQuery.length < 3) {
      setError('Query must be at least 3 characters');
      setLoading(false);
      return;
    }
    
    // Mode should already be normalized before reaching here
    const modeToUse = searchMode;
    
    setLoading(true);
    setError(null);
    
    // Create abort controller for timeout
    const controller = new AbortController();
    activeRequestRef.current = controller; // Store for potential cancellation
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
    
    const requestBody = {
      query: trimmedQuery, // Use trimmed query
      mode: modeToUse, // Use normalized mode
      location: loc || undefined, // Don't send empty string
    };
    
    try {
      const { data } = await api.post('/api/ai/interpret-to-pills', requestBody, {
        signal: controller.signal,
        timeout: 15000,
      });
      
      clearTimeout(timeoutId);
      
      if (data.success) {
        let enrichedFilters = data.filters || [];
        
        // Ensure Location filter is always present so users can add/edit it
        const hasLocationFilter = enrichedFilters.some(f => 
          f.id === 'state' || f.id === 'city' || f.id === 'location' || 
          f.id === 'country' || f.label?.toLowerCase().includes('location')
        );
        if (!hasLocationFilter) {
          enrichedFilters = [...enrichedFilters, {
            id: 'state',
            label: 'Location',
            apiField: 'locationState',
            type: 'multi-select',
            pills: [],
          }];
        }
        
        // Ensure Seniority filter is always present for people modes
        const isPeopleSearch = ['people', 'candidate', 'client'].includes(normalizedMode);
        if (isPeopleSearch) {
          const hasSeniorityFilter = enrichedFilters.some(f => 
            f.id === 'managementLevel' || f.label?.toLowerCase().includes('seniority')
          );
          if (!hasSeniorityFilter) {
            const insertIdx = enrichedFilters.findIndex(f => f.id === 'industryCodes' || f.id === 'state');
            const seniorityFilter = {
              id: 'managementLevel',
              label: 'Seniority',
              apiField: 'managementLevel',
              type: 'multi-select',
              pills: [],
            };
            if (insertIdx >= 0) {
              enrichedFilters = [
                ...enrichedFilters.slice(0, insertIdx),
                seniorityFilter,
                ...enrichedFilters.slice(insertIdx),
              ];
            } else {
              enrichedFilters = [...enrichedFilters, seniorityFilter];
            }
          }
        }
        
        setFilters(enrichedFilters);
        setOutputFields(data.outputFields || []);
        setConfidence(data.confidence);
        setEstimatedResults(data.estimatedResults);
        setSampleResults(data.sampleResults || []);
        setInterpretationNotes(data.aiInterpretation?.interpretationNotes || '');
        
        if (onFiltersChange) {
          setTimeout(() => {
            onFiltersChange(enrichedFilters, data.outputFields);
          }, 0);
        }
      } else {
        setError(data.error || 'Interpretation failed');
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }
      const msg = err.response?.data?.error ?? err.response?.data?.message ?? err.message;
      const status = err.response?.status;
      const displayMsg = status ? `HTTP ${status}: ${msg}` : msg;
      console.error('Pill interpretation error:', err);
      setError(displayMsg || 'Interpretation failed');
    } finally {
      // Clear active request ref if this was the active one
      if (activeRequestRef.current === controller) {
        activeRequestRef.current = null;
      }
      setLoading(false);
    }
  };
  
  // Update result estimate when filters change
  const updateEstimate = useCallback(async (currentFilters, currentOutputFields) => {
    try {
      const { data } = await api.post('/api/ai/estimate-results', {
        filters: currentFilters,
        outputFields: currentOutputFields,
        mode: normalizedMode,
      });
      // Track previous estimate for trend display
      if (estimatedResults !== null) {
        setPreviousEstimate(estimatedResults);
      }
      setEstimatedResults(data.estimatedCount);
      setSampleResults(data.sampleResults || []);
    } catch (err) {
      console.warn('Failed to update estimate:', err);
    }
  }, [normalizedMode, estimatedResults]);
  
  // Handle pill status change
  const handlePillChange = useCallback((filterId, pillId, currentStatus, isSingleSelect = false) => {
    const newFilters = filters.map(filter => {
      if (filter.id !== filterId) return filter;
      
      const newPills = filter.pills.map(pill => {
        // For single-select, reset others when selecting new
        if (isSingleSelect && pill.id !== pillId && currentStatus !== 'included') {
          return { ...pill, status: 'suggested' };
        }
        
        if (pill.id !== pillId) return pill;
        
        // Cycle through states: suggested → included → excluded → suggested
        const nextStatus = {
          'suggested': 'included',
          'included': 'excluded',
          'excluded': 'suggested',
        };
        
        return { ...pill, status: nextStatus[currentStatus] };
      });
      
      return { ...filter, pills: newPills };
    });
    
    console.log('🎯 PillBuilder: Pill changed', { filterId, pillId, currentStatus, newFilters });
    setFilters(newFilters);
    
    // Immediately notify parent of changes
    if (onFiltersChange) {
      onFiltersChange(newFilters, outputFields);
    }
    
    updateEstimate(newFilters, outputFields);
  }, [filters, outputFields, onFiltersChange, updateEstimate]);
  
  // Handle output field toggle
  const handleOutputFieldChange = useCallback((categoryId, fieldId) => {
    const newFields = outputFields.map(category => {
      if (category.id !== categoryId) return category;
      
      const newPills = category.pills.map(pill => {
        if (pill.id !== fieldId) return pill;
        
        // Toggle between included and suggested
        return {
          ...pill,
          status: pill.status === 'included' ? 'suggested' : 'included',
        };
      });
      
      return { ...category, pills: newPills };
    });
    
    setOutputFields(newFields);
    
    if (onFiltersChange) {
      setTimeout(() => {
        onFiltersChange(filters, newFields);
      }, 0);
    }
    
    updateEstimate(filters, newFields);
  }, [filters, outputFields, onFiltersChange, updateEstimate]);
  
  // Handle adding custom filter value
  const handleAddCustomPill = useCallback((filterId) => {
    if (!customFilterValue.trim()) {
      setAddingToFilter(null);
      return;
    }
    
    const newPillId = customFilterValue.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const newPill = {
      id: newPillId,
      label: customFilterValue.trim(),
      status: 'included', // Auto-include custom values
      apiValue: customFilterValue.trim(), // Use the raw value for API
    };
    
    // Check if filter exists
    const existingFilter = filters.find(f => f.id === filterId);
    
    let newFilters;
    if (existingFilter) {
      // Check if pill already exists
      const existingPill = existingFilter.pills.find(p => 
        p.label.toLowerCase() === customFilterValue.trim().toLowerCase()
      );
      
      if (existingPill) {
        // Just include the existing pill
        newFilters = filters.map(filter => {
          if (filter.id !== filterId) return filter;
          return {
            ...filter,
            pills: filter.pills.map(p => 
              p.id === existingPill.id ? { ...p, status: 'included' } : p
            ),
          };
        });
      } else {
        // Add new pill to existing filter
        newFilters = filters.map(filter => {
          if (filter.id !== filterId) return filter;
          return {
            ...filter,
            pills: [...filter.pills, newPill],
          };
        });
      }
    } else {
      // Create new filter category
      const filterConfig = CUSTOMIZABLE_FILTERS[filterId] || { label: filterId };
      newFilters = [
        ...filters,
        {
          id: filterId,
          label: filterConfig.label,
          type: 'multi-select',
          pills: [newPill],
        },
      ];
    }
    
    console.log('➕ PillBuilder: Added custom pill', { filterId, pill: newPill });
    setFilters(newFilters);
    setCustomFilterValue('');
    setAddingToFilter(null);
    
    if (onFiltersChange) {
      onFiltersChange(newFilters, outputFields);
    }
    
    updateEstimate(newFilters, outputFields);
  }, [customFilterValue, filters, outputFields, onFiltersChange, updateEstimate]);
  
  // Focus input when adding mode is activated
  useEffect(() => {
    if (addingToFilter && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [addingToFilter]);
  
  // Count active filters - must be defined before handleSearch
  const activeFilterCount = useMemo(() => {
    return filters.reduce((count, filter) => {
      const included = filter.pills?.filter(p => p.status === 'included').length || 0;
      const excluded = filter.pills?.filter(p => p.status === 'excluded').length || 0;
      return count + included + excluded;
    }, 0);
  }, [filters]);
  
  // Handle search execution - requires confirmation first
  const handleSearch = useCallback(() => {
    // Calculate active filter count inline to avoid dependency issues
    const currentActiveCount = filters.reduce((count, filter) => {
      const included = filter.pills?.filter(p => p.status === 'included').length || 0;
      const excluded = filter.pills?.filter(p => p.status === 'excluded').length || 0;
      return count + included + excluded;
    }, 0);
    
    // If not confirmed and we have filters, show confirmation gate
    if (!confirmed && filters.length > 0 && currentActiveCount > 0) {
      setNeedsConfirmation(true);
      setPendingSearch({ filters, outputFields, filterWeights, resultsLimit });
      return;
    }
    
    // Execute search after confirmation
    if (onSearch && confirmed) {
      onSearch(filters, outputFields, filterWeights, resultsLimit);
      setConfirmed(false); // Reset for next search
      setNeedsConfirmation(false);
    }
  }, [filters, outputFields, filterWeights, resultsLimit, onSearch, confirmed]);
  
  // Handle confirmation
  const handleConfirm = useCallback(() => {
    setConfirmed(true);
    setNeedsConfirmation(false);
    // Execute search immediately after confirmation
    if (onSearch && pendingSearch) {
      onSearch(pendingSearch.filters, pendingSearch.outputFields, pendingSearch.filterWeights, pendingSearch.resultsLimit);
      setPendingSearch(null);
      setConfirmed(false); // Reset for next search
    }
  }, [onSearch, pendingSearch]);
  
  // Handle clarification request
  const handleClarify = useCallback(() => {
    setNeedsConfirmation(false);
    setPendingSearch(null);
    // User can adjust filters, then try again
  }, []);
  
  // Count enabled output fields
  const enabledFieldCount = useMemo(() => {
    return outputFields.reduce((count, category) => {
      return count + (category.pills?.filter(p => p.status === 'included').length || 0);
    }, 0);
  }, [outputFields]);
  
  // Confidence indicator color
  const confidenceColor = confidence > 0.8 ? 'text-emerald-600' : 
                          confidence > 0.5 ? 'text-amber-600' : 
                          'text-red-600';
  
  // Don't render if no query
  if (!query || query.trim().length < 3) {
    return null;
  }
  
  return (
    <div 
      className={`pill-builder ${compact ? 'space-y-2' : 'space-y-3'}`}
      style={{ 
        animation: 'eq-slide-up 400ms cubic-bezier(0.16, 1, 0.3, 1)',
        fontFamily: "'Playfair Display', serif",
      }}
    >
      {/* Premium animation styles */}
      <style>{`
        @keyframes eq-slide-up {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes eq-thinking-pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
      
      {/* Header with confidence */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {loading ? (
            <div className="relative">
              <Loader2 className="w-4 h-4 text-[#5a20ff]" style={{ animation: 'spin 1s cubic-bezier(0.65, 0, 0.35, 1) infinite' }} />
              <div 
                className="absolute inset-0 w-4 h-4 rounded-full bg-[#5a20ff]/20"
                style={{ animation: 'eq-thinking-pulse 1.5s ease-in-out infinite' }}
              />
            </div>
          ) : (
            <div className="w-4 h-4 rounded bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center">
              <Wand2 className="w-2.5 h-2.5 text-white" />
            </div>
          )}
          <span className="text-xs text-black/50">
            {loading ? 'Scout is thinking...' : 'Understood as:'}
          </span>
          
          {confidence !== null && !loading && (
            <span className={`text-xs font-medium ${confidenceColor}`}>
              {Math.round(confidence * 100)}%
            </span>
          )}
        </div>
        
        {/* Compact estimate badge in header */}
        {!loading && (
          <ResultEstimateBadge 
            estimatedResults={estimatedResults} 
            loading={loading}
          />
        )}
      </div>
      
      {/* Prominent Result Estimate Indicator - KAI: Proactive guidance */}
      {!loading && (estimatedResults !== null || activeFilterCount > 0) && (
        <ResultEstimateIndicator
          estimatedResults={estimatedResults}
          previousResults={previousEstimate}
          loading={loading || isPredicting}
          size="default"
          showTrend={previousEstimate !== null}
          suggestions={getExpansionSuggestions().map(s => ({
            label: s.label,
            action: () => {
              // Remove the suggested filter
              const newFilters = filters.map(filter => {
                if (filter.id !== s.filterId) return filter;
                return {
                  ...filter,
                  pills: filter.pills.map(pill => 
                    pill.id === s.pillId ? { ...pill, status: 'suggested' } : pill
                  ),
                };
              });
              setFilters(newFilters);
              if (onFiltersChange) {
                onFiltersChange(newFilters, outputFields);
              }
              updateEstimate(newFilters, outputFields);
            },
          }))}
          onSuggestionClick={(suggestion) => suggestion.action?.()}
        />
      )}
      
      {/* Interpretation notes */}
      {interpretationNotes && !loading && (
        <div className="flex items-start gap-2 p-2 bg-[#5a20ff]/5 rounded-lg">
          <AlertCircle className="w-3 h-3 text-[#5a20ff] mt-0.5 flex-shrink-0" />
          <span className="text-xs text-[#5a20ff]">{interpretationNotes}</span>
        </div>
      )}
      
      {/* Error state */}
      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-xs text-red-600">{error}</span>
        </div>
      )}
      
      {/* Filter Pills Section */}
      {!loading && filters.length > 0 && (
        <div className="space-y-3">
          {/* Section header */}
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-left"
            >
              <SlidersHorizontal className="w-3.5 h-3.5 text-black/40" />
              <span className="text-xs font-medium text-black/70">Filters</span>
              {activeFilterCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 bg-[#5a20ff]/10 text-[#5a20ff] rounded-full">
                  {activeFilterCount} active
                </span>
              )}
              {showFilters ? (
                <ChevronUp className="w-3 h-3 text-black/40" />
              ) : (
                <ChevronDown className="w-3 h-3 text-black/40" />
              )}
            </button>
            
            {/* Weight toggle */}
            {showFilters && (
              <button
                onClick={() => setShowWeights(!showWeights)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] transition-colors ${
                  showWeights 
                    ? 'bg-[#5a20ff]/10 text-[#5a20ff]' 
                    : 'bg-black/5 text-black/50 hover:bg-black/10'
                }`}
              >
                <Gauge className="w-3 h-3" />
                Priority
              </button>
            )}
          </div>
          
          {showFilters && (
            <div className="space-y-3 pl-5">
              {filters.map(filter => {
                const isCustomizable = CUSTOMIZABLE_FILTERS[filter.id];
                const isAddingToThis = addingToFilter === filter.id;
                
                return (
                  <div key={filter.id} className="space-y-2">
                    <div className={`flex items-start gap-3 ${showWeights ? 'justify-between' : ''}`}>
                      <div className="flex-1">
                        {/* Filter label and pills */}
                        <div className="pill-group">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-xs font-medium text-black/60">{filter.label}</span>
                            {filter.pills.filter(p => p.status === 'included').length > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                                {filter.pills.filter(p => p.status === 'included').length} selected
                              </span>
                            )}
                            {filter.type === 'single-select' && (
                              <span className="text-[10px] text-black/40">(pick one)</span>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-1.5 items-center">
                            {filter.pills.map(pill => {
                              // Get predictive state for this pill
                              const pillPrediction = getPillState(filter.id, pill.id);
                              
                              return (
                                <Pill
                                  key={pill.id}
                                  label={pill.label}
                                  status={pill.status}
                                  onClick={() => handlePillChange(filter.id, pill.id, pill.status, filter.type === 'single-select')}
                                  size="default"
                                  // Predictive filtering props
                                  predictiveState={pillPrediction.state}
                                  estimatedCount={pillPrediction.estimatedCount}
                                  showEstimate={pillPrediction.state === 'warning'}
                                />
                              );
                            })}
                            
                            {/* Add button for customizable filters */}
                            {isCustomizable && !isAddingToThis && (
                              <button
                                onClick={() => {
                                  setAddingToFilter(filter.id);
                                  setCustomFilterValue('');
                                }}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium
                                  bg-white border border-dashed border-[#5a20ff]/30 text-[#5a20ff]
                                  hover:bg-[#5a20ff]/5 hover:border-[#5a20ff]/50 transition-all"
                                title={`Add custom ${filter.label.toLowerCase()}`}
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Add
                              </button>
                            )}
                            
                            {/* Input field when adding */}
                            {isAddingToThis && (
                              <div className="flex items-center gap-1">
                                <input
                                  ref={customInputRef}
                                  type="text"
                                  value={customFilterValue}
                                  onChange={(e) => setCustomFilterValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleAddCustomPill(filter.id);
                                    if (e.key === 'Escape') {
                                      setAddingToFilter(null);
                                      setCustomFilterValue('');
                                    }
                                  }}
                                  placeholder={isCustomizable.placeholder}
                                  className="w-32 px-2 py-1 text-xs rounded-full border border-[#5a20ff]/30 
                                    focus:border-[#5a20ff] focus:outline-none focus:ring-1 focus:ring-[#5a20ff]/20"
                                />
                                <button
                                  onClick={() => handleAddCustomPill(filter.id)}
                                  className="p-1 rounded-full bg-[#5a20ff] text-white hover:bg-[#5a20ff]/80 transition-colors"
                                  title="Add"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => {
                                    setAddingToFilter(null);
                                    setCustomFilterValue('');
                                  }}
                                  className="p-1 rounded-full bg-black/10 text-black/50 hover:bg-black/20 transition-colors"
                                  title="Cancel"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Weight slider for this filter */}
                      {showWeights && (
                        <div className="flex-shrink-0 w-24">
                          <WeightSlider
                            value={filterWeights[filter.id] || 50}
                            onChange={(weight) => {
                              setFilterWeights(prev => ({
                                ...prev,
                                [filter.id]: weight
                              }));
                              // Notify parent of weight change
                              if (onFiltersChange) {
                                onFiltersChange(filters, outputFields, {
                                  ...filterWeights,
                                  [filter.id]: weight
                                });
                              }
                            }}
                            size="compact"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Quick add buttons for missing filter categories */}
              {!loading && (
                <div className="pt-2 border-t border-black/5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-black/40">Add filter:</span>
                    {QUICK_ADD_FILTERS.filter(qf => !filters.find(f => f.id === qf.id)).map(qf => (
                      <button
                        key={qf.id}
                        onClick={() => {
                          setAddingToFilter(qf.id);
                          setCustomFilterValue('');
                          // If filter doesn't exist, we'll create it when adding
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium
                          bg-black/5 text-black/50 hover:bg-[#5a20ff]/10 hover:text-[#5a20ff] transition-all"
                      >
                        <Plus className="w-3 h-3" />
                        {qf.label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Input for adding new filter category */}
                  {addingToFilter && !filters.find(f => f.id === addingToFilter) && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-black/60">
                        {CUSTOMIZABLE_FILTERS[addingToFilter]?.label || addingToFilter}:
                      </span>
                      <input
                        ref={customInputRef}
                        type="text"
                        value={customFilterValue}
                        onChange={(e) => setCustomFilterValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleAddCustomPill(addingToFilter);
                          if (e.key === 'Escape') {
                            setAddingToFilter(null);
                            setCustomFilterValue('');
                          }
                        }}
                        placeholder={CUSTOMIZABLE_FILTERS[addingToFilter]?.placeholder || 'Enter value...'}
                        className="flex-1 max-w-[200px] px-2 py-1 text-xs rounded-full border border-[#5a20ff]/30 
                          focus:border-[#5a20ff] focus:outline-none focus:ring-1 focus:ring-[#5a20ff]/20"
                      />
                      <button
                        onClick={() => handleAddCustomPill(addingToFilter)}
                        className="p-1 rounded-full bg-[#5a20ff] text-white hover:bg-[#5a20ff]/80 transition-colors"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => {
                          setAddingToFilter(null);
                          setCustomFilterValue('');
                        }}
                        className="p-1 rounded-full bg-black/10 text-black/50 hover:bg-black/20 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Output Fields Section */}
      {showOutputFields && !loading && outputFields.length > 0 && (
        <div className="space-y-2">
          {/* Section header */}
          <button 
            onClick={() => setShowOutputConfig(!showOutputConfig)}
            className="flex items-center gap-2 w-full text-left"
          >
            <TableProperties className="w-3.5 h-3.5 text-black/40" />
            <span className="text-xs font-medium text-black/70">Output Fields</span>
            <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-full">
              {enabledFieldCount} fields
            </span>
            {showOutputConfig ? (
              <ChevronUp className="w-3 h-3 text-black/40 ml-auto" />
            ) : (
              <ChevronDown className="w-3 h-3 text-black/40 ml-auto" />
            )}
          </button>
          
          {showOutputConfig && (
            <div className="space-y-3 pl-5">
              {outputFields.map(category => (
                <div key={category.id} className="space-y-1">
                  <span className="text-xs font-medium text-black/50">{category.label}</span>
                  <div className="flex flex-wrap gap-1">
                    {category.pills.map(field => (
                      <OutputFieldPill
                        key={field.id}
                        label={field.label}
                        enabled={field.status === 'included'}
                        isDefault={field.default}
                        onClick={() => handleOutputFieldChange(category.id, field.id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Sample Results Preview */}
      {sampleResults.length > 0 && !loading && (
        <div className="p-2 bg-gray-50 rounded-lg space-y-1">
          <span className="text-[10px] font-medium text-black/40 uppercase tracking-wide">
            Sample Results
          </span>
          {sampleResults.slice(0, 3).map((sample, idx) => (
            <div key={idx} className="flex items-center gap-2 text-xs">
              <span className="font-medium text-black/80">{sample.name}</span>
              {sample.title && (
                <span className="text-black/50">• {sample.title}</span>
              )}
              {sample.company && (
                <span className="text-black/40">@ {sample.company}</span>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Confirmation Gate */}
      {needsConfirmation && !loading && filters.length > 0 && (
        <div className="p-4 bg-gradient-to-br from-[#5a20ff]/5 to-[#52dcb3]/5 rounded-lg border border-[#5a20ff]/20 space-y-3">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center flex-shrink-0 mt-0.5">
              <Wand2 className="w-3 h-3 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-black/80 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Scout understands:
              </p>
              <div className="text-xs text-black/60 space-y-1">
                {filters.slice(0, 3).map(filter => {
                  const included = filter.pills?.filter(p => p.status === 'included') || [];
                  if (included.length === 0) return null;
                  return (
                    <div key={filter.id} className="flex items-center gap-1">
                      <span className="text-black/40">•</span>
                      <span>{filter.label}: {included.map(p => p.label).join(', ')}</span>
                    </div>
                  );
                })}
                {estimatedResults !== null && (
                  <div className="mt-2 pt-2 border-t border-black/10">
                    <span className="font-medium text-[#5a20ff]">~{estimatedResults.toLocaleString()} expected matches</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={handleConfirm}
              className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white font-medium text-sm hover:opacity-90 active:scale-[0.99] transition-all"
            >
              <Check className="w-4 h-4" />
              This looks right
            </button>
            <button
              onClick={handleClarify}
              className="px-4 py-2 rounded-lg bg-white border border-black/10 text-black/60 font-medium text-sm hover:bg-black/5 transition-colors"
            >
              Let me clarify
            </button>
          </div>
        </div>
      )}
      
      {/* Search Button - only show if not in confirmation state */}
      {onSearch && !loading && filters.length > 0 && !needsConfirmation && (
        <button
          onClick={handleSearch}
          disabled={activeFilterCount === 0}
          className={`
            w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg
            font-medium text-sm transition-all
            ${activeFilterCount > 0 
              ? 'bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white hover:opacity-90 active:scale-[0.99]' 
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          <Search className="w-4 h-4" />
          Search with {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
}

export default PillBuilder;
